import React from 'react';
import { Brain, Check } from 'lucide-react';
import Select from './Select';
import { Skill } from '../../types';

interface SkillSelectorProps {
  skills: Skill[];
  selectedSkillId: string | null | undefined;
  onSkillChange: (skillId: string) => void;
  disabled?: boolean;
  className?: string;
}

const SkillSelector: React.FC<SkillSelectorProps> = ({
  skills,
  selectedSkillId,
  onSkillChange,
  disabled = false,
  className = '',
}) => {
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const prevSkillIdRef = React.useRef(selectedSkillId);

  // Find selected skill for display
  const selectedSkill = skills.find(s => s.skill_id === selectedSkillId) || skills.find(s => s.is_default) || skills[0];
  
  // Convert skills to select options
  const options = skills.map(skill => ({
    value: skill.skill_id,
    label: skill.name,
  }));

  // Track skill changes and trigger animation
  React.useEffect(() => {
    if (prevSkillIdRef.current !== selectedSkillId && prevSkillIdRef.current !== null) {
      // Skill changed - show success animation
      setIsUpdating(false);
      setShowSuccess(true);
      const timer = setTimeout(() => {
        setShowSuccess(false);
      }, 500);
      return () => clearTimeout(timer);
    }
    prevSkillIdRef.current = selectedSkillId;
  }, [selectedSkillId]);

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSkillId = e.target.value;
    if (newSkillId && newSkillId !== selectedSkillId) {
      setIsUpdating(true);
      onSkillChange(newSkillId);
    }
  };

  return (
    <div className={`flex items-center space-x-1.5 ${className}`}>
      <div className="relative">
        <Brain 
          className={`h-3.5 w-3.5 transition-all duration-300 ${
            showSuccess 
              ? 'text-green-500 scale-110' 
              : isUpdating 
                ? 'text-primary-500 animate-pulse' 
                : 'text-gray-500'
          }`} 
        />
        {showSuccess && (
          <Check 
            className="absolute -top-1 -right-1 h-3 w-3 text-green-500 animate-ping"
            style={{ animationDuration: '0.5s' }}
          />
        )}
      </div>
      <Select
        value={selectedSkillId || selectedSkill?.skill_id || ''}
        onChange={handleChange}
        options={options}
        disabled={disabled}
        className="min-w-[148px] h-8 py-1 text-xs rounded-md"
        placeholder="Select Agent"
      />
    </div>
  );
};

export default SkillSelector;
